import AbstractPlayerEngine from 'components/pages/playback/player-engines/AbstractPlayerEngine';
import { AppData, Registry } from '@lightningjs/sdk';
import BrightcovePlayerEventConsumer from 'components/pages/playback/player-event-consumers/BrightcovePlayerEventConsumer';
import type {
  BcPlayer,
  BcSsaiIntegration,
  BrightcoveVideoModel,
} from 'types/brightcove';
import { BcPlayerEvents } from 'types/player';
import { getAdTagParams, getCommonLanguageCode } from 'support/playerUtils';
import NonNativeTextTrackHandler from './NonNativeTextTrackHandler';
import {
  ErrorCode,
  reportError,
} from 'services/analytics/reportingServicePage';
import { is1280x720p } from 'support/generalUtils';

const CONTAINER_ID = 'bc-container';

const CUE_POLLING_TIMEOUT = 1000;

type BcTextTrack = {
  src?: string;
} & TextTrack;

export enum BrightcoveState {
  Idle = 0,
  Paused = 1,
  Playing = 2,
  Buffering = 3,
}

export default class BrightcovePlayerEngine extends AbstractPlayerEngine<
  never,
  [token: string, videoId: string, guid: string | undefined]
> {
  /*
  TODO: video model and ssai integration magic have some useful properties, if unused remove these
  note brightcoveVideoModel.cuePoints does not give accurate cue points
  */
  private brightcoveVideoModel: BrightcoveVideoModel | undefined = undefined;
  private ssaiIntegration: BcSsaiIntegration | null = null;

  private player: BcPlayer | null = null;
  private loadingSdk: Promise<void>;
  // this value is a bit misleading. We're using the internal subtitles from the player, but loading them externally
  override useExternalSubtitles = true;
  private nonNativeTextTrackHandler: NonNativeTextTrackHandler | null = null;
  private trackPolling: Record<string, number> = {};
  private videoJsObserver: MutationObserver | null = null;
  private vhsInstance = null;

  constructor() {
    super();

    this.loadingSdk = AppData!.brightcove.sdk!.then(() => {
      this.init();
    });
  }

  consumer(consumer: BrightcovePlayerEventConsumer) {
    this.loadingSdk.then(() => {
      consumer.patch({ brightcoveInstance: this.player! });
    });
  }

  isPlaying(): boolean {
    if (!this.player) return false;

    // Intermittently for BC, the CurrentPlaybackState is not always up to date,
    // always returns 'playing' state even when paused.
    // Using HTMLVideoElement method resolves this.

    const element = this.player.getVideoElement();
    if (!element) return false;
    return !element?.paused;
  }

  play(): void {
    this.player?.play();
  }

  pause(): void {
    this.player?.pause();
  }

  seek(rawSeekTime: number) {
    if (!this.player) return;
    const element = this.player.getVideoElement();
    if (!element) return;

    element.currentTime = rawSeekTime;
  }

  getCurrentTime(): number {
    return this.player?.getCurrentTime() ?? 0;
  }

  updateDimensions(dimensions: {
    x?: number;
    y?: number;
    w?: number;
    h?: number;
  }) {
    this.loadingSdk.then(() => {
      if (!this.player) return;

      // TODO: implement x/y positioning
      const { w, h, x, y } = dimensions;
      const width = is1280x720p() ? 1280 : w ?? 1920;
      const height = is1280x720p() ? 720 : h ?? 1080;

      // TODO: remove, this shouldn't required but "updateConfiguration" isn't working
      const videoElement = this.player.getVideoElement()!;
      videoElement.width = width;
      videoElement.height = height;
      // videoElement.style.position

      this.player.updateConfiguration({
        ui: {
          width,
          height,
        },
      });
    });
  }

  getSrcUrl(): string {
    const srcUrl = this.getVhsInstance()?.playlists?.srcUrl;
    if (!srcUrl) return '';

    // remove parameters part from the url
    return srcUrl.split('?')[0];
  }

  override async destroy() {
    super.destroy();

    this.clearPollingIntervals();

    this.videoJsObserver?.disconnect();
    this.videoJsObserver = null;

    await this.loadingSdk; // we should wait for it to load before destroying
    this.player?.removeAllEventListeners();
    this.player?.dispose(); // unfortunately dispose does not remove the html elements
    this.ssaiIntegration?.dispose();

    const containerElement = document.getElementById(CONTAINER_ID);
    containerElement?.remove();

    this.player = null;
    this.nonNativeTextTrackHandler = null;
  }

  override async load(
    startTime: number | undefined,
    token: string,
    bcVideoId: string,
    guid: string | undefined,
  ) {
    await this.loadingSdk;
    if (!this.player) throw new Error('player undefined');

    const adParams = await getAdTagParams(
      AppData?.adParams?.platform.brightcove_params.vmapURLParams,
      guid ?? '',
      {
        w: 1920,
        h: 1080,
      },
    );

    this.player.updateConfiguration({
      brightcove: {
        authToken: token,
      },
      ui: {
        width: 1920,
        height: 1080,
        //@ts-ignore
        language: 'en',
        playsinline: false,
      },
      integrations: {
        ssai: {
          vmapURLParams: {
            ...adParams,
          },
        },
      },
    });

    this.subtitles = [];

    this.player.addEventListener('playerTextTracksChanged', arg => {
      (arg.tracks as BcTextTrack[]).forEach(track => {
        const isCorrectKind =
          track.kind === 'captions' || track.kind === 'subtitles';

        const hasSrc = track.src;
        const hasBeenAdded = this.isTextTrackAdded(track);
        const isPollingCue = track.id in this.trackPolling;

        if (isCorrectKind && hasSrc && !hasBeenAdded && !isPollingCue) {
          this.pollEmptyTextTrack(track); // cues aren't added right away when tracks are created
        }
      });
    });

    const { abort, promise } = this.player.getVideoByIdFromPlaybackApi({
      videoId: bcVideoId,
    });

    try {
      const brightcoveVideoModel = await promise;
      this.brightcoveVideoModel = brightcoveVideoModel;

      this.player.loadBrightcoveVideoModel(brightcoveVideoModel);
    } catch (error) {
      console.error('Error loading video:', error);
      reportError({
        error,
        userFacing: true,
        code: ErrorCode.c34,
      });
    }

    // We call play here to prevent a stutter when a start time is provided
    this.player?.play();
  }

  override onAdaption(handleAdaption: (bitrate: number) => void): void {
    this.player?.addEventListener(
      BcPlayerEvents.PlayerQualityLevelsChanged,
      () => {
        const attributes = this.getCurrentMediaAttributes();
        if (attributes) {
          this.currentBitrate = attributes.BANDWIDTH / 1000;
          handleAdaption(this.currentBitrate);
        }
      },
    );
  }

  override onError(
    handleError: (isFatal: boolean, errorMessage: string) => void,
  ): void {
    this.player?.addEventListener(
      BcPlayerEvents.PlayerError,
      (playerError: { type: any; error: any }) => {
        const type = playerError.type;
        const error = playerError.error;
        handleError(true, `${type}: ${error.category} ${error.code}`);
      },
    );
  }

  override getAudioOptions(): string[] {
    const options: string[] = [];
    const audioTracks = this.player?.getAudioTracks();
    audioTracks?.forEach(track => {
      if (track.language) options.push(track.language);
    });
    return options;
  }

  override setAudioTrack(lang: string): void {
    const audioTracks = this.player?.getAudioTracks();
    const targetTrack = audioTracks?.find(track => track.language === lang);

    if (targetTrack) this.player?.selectAudioTrack(targetTrack);
  }

  override enableCc(lang: string): void {
    const commonLanguageCode = getCommonLanguageCode(lang);

    const enabledNonNativeTextTrack =
      this.nonNativeTextTrackHandler?.enableNonNativeTextTrack(
        commonLanguageCode,
      );

    if (!enabledNonNativeTextTrack) return;

    const id = this.nonNativeTextTrackHandler?.getSelectedTrackId();
    const selectedTrack = this.player
      ?.getTextTracks()
      .find(track => track.id === id);

    if (!selectedTrack || !selectedTrack.cues) return;

    const trackList = selectedTrack.cues;
    const cues: VTTCue[] = [];
    for (let i = 0; i < trackList.length; i++) {
      // The cues aren't actually VTTCues, they're the VideoJs cues. We can't process these directly so we have to make new VTTCues
      const { startTime, endTime, text, ...rest } = trackList[i]! as VTTCue;
      const cueId = `${id}-${i}`;

      const cue = new VTTCue(startTime, endTime, text);

      cue.id = cueId;
      cue.align = rest.align;
      cue.line = rest.line;
      cue.lineAlign = rest.lineAlign;
      cue.position = rest.position;
      cue.positionAlign = rest.positionAlign;
      cue.region = rest.region;
      cue.size = rest.size;
      cue.snapToLines = rest.snapToLines;
      cue.vertical = rest.vertical;
      cues.push(cue);
    }

    this.nonNativeTextTrackHandler!.addNonNativeTextTrackCues(cues);
  }

  override disableCc(): void {
    this.nonNativeTextTrackHandler!.disableNonNativeTextTrack();
  }

  protected override extractSubtitleOptions(): string[] {
    // throw new Error('extractSubtitleOptions should not be called');
    return [];
  }
  protected override getPlayerMedia(): HTMLMediaElement | null {
    return this.player?.getVideoElement() ?? null;
  }

  private async init() {
    if (!!this.player) {
      this.destroy();
    }

    // This should already be resolved, awaiting as a precaution
    const { Player, IntegrationsManager, SsaiIntegrationFactory } =
      await AppData!.brightcove.sdk!;
    IntegrationsManager.registerSsaiIntegrationFactory(SsaiIntegrationFactory);

    this.player = new Player!({
      // Account ID parameter is required to initialize a player
      // @ts-ignore
      accountId: AppData?.brightcove.accountId,
    });

    this.player?.updateConfiguration({
      brightcove: {
        adConfigId:
          AppData?.adParams?.platform.brightcove_params.ad_config_id ??
          AppData?.brightcove.adConfigId,
      },
      integrations: {
        ssai: {
          hideOverlays: true,
        },
      },
    });

    const { ssaiIntegration } = this.player!.getIntegrationsManager();
    this.ssaiIntegration = ssaiIntegration;

    const container = document.createElement('div');
    container.id = CONTAINER_ID;
    if (is1280x720p()) {
      container.style.width = '1280px';
      container.style.height = '720px';
    } else {
      container.style.width = '1920px';
      container.style.height = '1080px';
    }

    document.body.appendChild(container);
    this.player!.attach(container);
    this.disableBrightcoveUiElements();

    const videoElement = this.player!.getVideoElement()!;

    this.nonNativeTextTrackHandler = new NonNativeTextTrackHandler(
      videoElement,
    );

    this.clearPollingIntervals();
  }

  private isTextTrackAdded(track: BcTextTrack): boolean {
    const addedIds = this.nonNativeTextTrackHandler?.nonNativeTextTrackIds;

    return (
      addedIds?.includes(track.id) ||
      this.subtitles.includes(getCommonLanguageCode(track.language))
    );
  }

  private addTextTracks(tracks: BcTextTrack[]) {
    if (tracks.length > 0) {
      tracks.forEach(track =>
        this.subtitles.push(getCommonLanguageCode(track.language)),
      );
      this.nonNativeTextTrackHandler?.addTextTracks(tracks);
      this.updateSubtitles();
    }
  }

  private pollEmptyTextTrack(track: BcTextTrack) {
    if (track.id in this.trackPolling) {
      Registry.clearInterval(this.trackPolling[track.id]);
    }

    const interval = Registry.setInterval(() => {
      if (track.cues && track.cues.length > 0) {
        Registry.clearInterval(interval);
        delete this.trackPolling[track.id];

        // this should always be true
        if (!this.isTextTrackAdded(track)) {
          this.addTextTracks([track]);
        }
      }
    }, CUE_POLLING_TIMEOUT);

    this.trackPolling[track.id] = interval;
  }

  private clearPollingIntervals() {
    Object.values(this.trackPolling).forEach(interval => {
      Registry.clearInterval(interval);
    });

    this.trackPolling = {};
  }

  private disableElement(element: Element) {
    if (element.tagName !== 'DIV' && element.tagName !== 'BUTTON') return;
    (element as HTMLElement).style.display = 'none';
    element.setAttribute('aria-disabled', 'true');

    if (element.tagName === 'BUTTON') {
      (element as HTMLButtonElement).disabled = true;
    }
  }

  private disableBrightcoveUiElements() {
    const videoJsContainer = document
      .getElementById(CONTAINER_ID)
      ?.getElementsByTagName('VIDEO-JS')[0];

    if (!videoJsContainer) return;
    // (videoJsContainer as any).player; // video js player reference!

    if (is1280x720p()) {
      (videoJsContainer as HTMLElement).style.width = '1280px';
      (videoJsContainer as HTMLElement).style.height = '720px';
    }
    for (const element of videoJsContainer.children) {
      this.disableElement(element);
    }

    // listen for any elements being added to video js and disable them
    this.videoJsObserver = new MutationObserver(mutationsList => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              this.disableElement(node as Element);
            }
          });
        }
      }
    });

    // listens for any elements being added to the video js container so we can disable them
    this.videoJsObserver.observe(videoJsContainer, {
      childList: true,
      subtree: false,
    });
  }

  private getVhsInstance = (): any => {
    if (!this.vhsInstance) {
      const playerElement: any = document.querySelector('.video-js');
      this.vhsInstance = playerElement?.player.tech(true).vhs;
    }

    return this.vhsInstance;
  };

  private getCurrentMediaAttributes = () => {
    const vhs = this.vhsInstance || this.getVhsInstance();
    return vhs?.playlists?.media().attributes;
  };
}
